import { memo, useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import {
    ENGAGEMENT_TEAM_ROLES,
    EngagementTeamRole,
} from "@common/types/taskTemplateAssigneeRole";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { AvatarTooltip } from "@components/avatar/AvatarTooltip";
import { AvatarGroup } from "@design-system/Avatar/AvatarGroup";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import { ASSIGNEE_ROLE_LABEL } from "../closeTemplateTasksDrawer.constants";
import { ServiceTemplateScheduleRow } from "../serviceTemplateSchedulesViewConfig";
import {
    EngagementTeamMember,
    ServiceTemplateScheduleCallbacks,
    ServiceTemplateScheduleTableOptions,
} from "../ServiceTemplateSchedulesList";
import {
    EMPTY_TEMPLATE_ESTIMATE_STATS,
    getRoleEstimatedMinutes,
} from "../templateEstimateStats";
import styles from "./ServiceTemplateScheduleEngagementTeamCell.module.scss";

const ROLE_TO_FIELD = {
    preparer: "preparerId",
    reviewer: "reviewerId",
    manager: "managerId",
} as const;

interface RoleAssignment {
    role: EngagementTeamRole;
    minutes: number;
}

type TeamSlot =
    | {
          kind: "member";
          member: EngagementTeamMember;
          roles: RoleAssignment[];
      }
    | {
          kind: "unassigned";
          role: EngagementTeamRole;
          minutes: number;
      };

const formatRoleEstimate = (role: EngagementTeamRole, minutes: number) => {
    const roleLabel = ASSIGNEE_ROLE_LABEL[role];
    if (minutes <= 0) {
        return roleLabel;
    }
    return `${roleLabel} · ${formatDuration(minutes)}`;
};

const MemberTooltip = ({
    displayName,
    roles,
}: {
    displayName: string;
    roles: RoleAssignment[];
}) => {
    if (roles.length === 1) {
        const [{ role, minutes }] = roles;
        return (
            <>
                {displayName} · {formatRoleEstimate(role, minutes)}
            </>
        );
    }

    return (
        <VStack gap={4}>
            <span>{displayName}</span>
            {roles.map(({ role, minutes }) => (
                <HStack key={role} gap={12} justify="between">
                    <span>{ASSIGNEE_ROLE_LABEL[role]}</span>
                    {minutes > 0 ? (
                        <span>{formatDuration(minutes)}</span>
                    ) : null}
                </HStack>
            ))}
        </VStack>
    );
};

const getDisplayName = (member: EngagementTeamMember) =>
    member.preferredName ?? member.name;

const getSlotKey = (slot: TeamSlot) =>
    slot.kind === "member"
        ? `member-${slot.member.id}`
        : `unassigned-${slot.role}`;

const getSlotLabel = (slot: TeamSlot) =>
    slot.kind === "member"
        ? `${getDisplayName(slot.member)} · ${slot.roles
              .map(({ role, minutes }) => formatRoleEstimate(role, minutes))
              .join(", ")}`
        : `Unassigned · ${formatRoleEstimate(slot.role, slot.minutes)}`;

const renderSlot = (slot: TeamSlot) =>
    slot.kind === "member" ? (
        <AvatarTooltip
            content={
                <MemberTooltip
                    displayName={getDisplayName(slot.member)}
                    roles={slot.roles}
                />
            }
        >
            <UserAvatar user={slot.member} />
        </AvatarTooltip>
    ) : (
        <AvatarTooltip
            content={`Unassigned · ${formatRoleEstimate(slot.role, slot.minutes)}`}
        >
            <span className={styles.unassignedAvatar} aria-hidden />
        </AvatarTooltip>
    );

export const ServiceTemplateScheduleEngagementTeamCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ServiceTemplateScheduleRow, string>) => {
        const meta = useViewTableMeta<
            ServiceTemplateScheduleCallbacks,
            ServiceTemplateScheduleTableOptions
        >(table);

        const memberById = meta.options?.memberById;
        const estimateStats =
            meta.options?.templateEstimateById?.get(row.original.templateId) ??
            EMPTY_TEMPLATE_ESTIMATE_STATS;

        const teamSlots = useMemo((): TeamSlot[] => {
            if (row.original.rowType === "group") {
                return [];
            }

            const schedule = row.original;
            const slots: TeamSlot[] = [];
            const slotByMemberId = new Map<
                number,
                TeamSlot & { kind: "member" }
            >();

            for (const role of ENGAGEMENT_TEAM_ROLES) {
                const field = ROLE_TO_FIELD[role];
                const memberId = schedule[field];
                const member =
                    memberId != null ? memberById?.get(memberId) : undefined;
                const minutes = getRoleEstimatedMinutes(estimateStats, role);

                if (!member && minutes === 0) {
                    continue;
                }

                if (!member) {
                    slots.push({ kind: "unassigned", role, minutes });
                    continue;
                }

                const existing = slotByMemberId.get(member.id);
                if (existing) {
                    existing.roles.push({ role, minutes });
                    continue;
                }

                const slot: TeamSlot & { kind: "member" } = {
                    kind: "member",
                    member,
                    roles: [{ role, minutes }],
                };
                slotByMemberId.set(member.id, slot);
                slots.push(slot);
            }

            return slots;
        }, [row.original, memberById, estimateStats]);

        if (row.original.rowType === "group" || teamSlots.length === 0) {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell>
                <AvatarGroup
                    items={teamSlots}
                    getKey={getSlotKey}
                    getLabel={getSlotLabel}
                    renderAvatar={renderSlot}
                    max={3}
                    size="sm"
                />
            </ViewTableCell>
        );
    },
);

ServiceTemplateScheduleEngagementTeamCell.displayName =
    "ServiceTemplateScheduleEngagementTeamCell";
